// Single source of truth for the Flow Builder node catalog. Returns the same
// categorized structure used by FlowToolbar's sidebar and by the Cmd+K
// command palette. Pass the `t` function from useI18n so the labels are
// reactive to locale switching.

export const getNodeCatalog = t => [
  {
    key: 'triggers',
    label: t('PIPELINE.FLOW_BUILDER.TOOLBAR.TRIGGERS'),
    icon: 'trigger',
    items: [
      {
        type: 'trigger',
        data: { type: 'item_created', event_type: 'pipeline_item_created' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.item_created'),
      },
      {
        type: 'trigger',
        data: {
          type: 'conversation_created',
          event_type: 'conversation_created',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.conversation_created'),
      },
      {
        type: 'trigger',
        data: { type: 'message_received', event_type: 'message_created' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.message_received'),
      },
      {
        type: 'trigger',
        data: { type: 'item_moved', event_type: 'pipeline_card_stage_changed' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.item_moved'),
      },
      {
        type: 'trigger',
        data: {
          type: 'conversation_resolved',
          event_type: 'conversation_resolved',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.conversation_resolved'),
      },
      {
        type: 'trigger',
        data: { type: 'contact_created', event_type: 'contact_created' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.contact_created'),
      },
      {
        type: 'trigger',
        data: { type: 'first_reply_sent', event_type: 'first_reply_created' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.first_reply'),
      },
      {
        type: 'trigger',
        data: { type: 'item_won', event_type: 'pipeline_card_won' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.item_won'),
      },
      {
        type: 'trigger',
        data: { type: 'scheduled' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.scheduled'),
      },
      {
        type: 'trigger',
        data: { type: 'webhook_received' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.webhook'),
      },
      // Appointment triggers — wired to FlowBuilderListener in this sprint.
      // Use cases: D-1 reminder, no-show recovery, completed-survey, etc.
      {
        type: 'trigger',
        data: {
          type: 'appointment_created',
          event_type: 'appointment_created',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.appointment_created'),
      },
      {
        type: 'trigger',
        data: {
          type: 'appointment_confirmed',
          event_type: 'appointment_confirmed',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.appointment_confirmed'),
      },
      {
        type: 'trigger',
        data: {
          type: 'appointment_completed',
          event_type: 'appointment_completed',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.appointment_completed'),
      },
      {
        type: 'trigger',
        data: {
          type: 'appointment_no_show',
          event_type: 'appointment_no_show',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.appointment_no_show'),
      },
      {
        type: 'trigger',
        data: {
          type: 'appointment_cancelled',
          event_type: 'appointment_cancelled',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.appointment_cancelled'),
      },
      {
        type: 'trigger',
        data: {
          type: 'appointment_rescheduled',
          event_type: 'appointment_rescheduled',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.appointment_rescheduled'),
      },
      // Lead score / SLA / Captain triggers — listener handlers are wired;
      // dispatcher hookup in LeadScoring / SLA tracker / Captain pending.
      {
        type: 'trigger',
        data: {
          type: 'lead_score_threshold_crossed',
          event_type: 'lead_score_threshold_crossed',
        },
        label: t(
          'PIPELINE.FLOW_BUILDER.NODE.TRIGGER.lead_score_threshold_crossed'
        ),
      },
      {
        type: 'trigger',
        data: {
          type: 'sla_first_response_breach',
          event_type: 'sla_first_response_breach',
        },
        label: t(
          'PIPELINE.FLOW_BUILDER.NODE.TRIGGER.sla_first_response_breach'
        ),
      },
      {
        type: 'trigger',
        data: {
          type: 'sla_resolution_breach',
          event_type: 'sla_resolution_breach',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.sla_resolution_breach'),
      },
      {
        type: 'trigger',
        data: {
          type: 'captain_intent_detected',
          event_type: 'captain_intent_detected',
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.TRIGGER.captain_intent_detected'),
      },
    ],
  },
  {
    key: 'conditions',
    label: t('PIPELINE.FLOW_BUILDER.TOOLBAR.CONDITIONS'),
    icon: 'condition',
    items: [
      {
        type: 'condition',
        data: { field: 'conversation.status', operator: 'equals', value: '' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.STATUS'),
      },
      {
        type: 'condition',
        data: { field: 'conversation.priority', operator: 'equals', value: '' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.PRIORITY'),
      },
      {
        type: 'condition',
        data: { field: 'message.content', operator: 'contains', value: '' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.MESSAGE'),
      },
      {
        type: 'condition',
        data: { field: 'conversation.label', operator: 'equals', value: '' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.LABEL_FIELD'),
      },
      {
        type: 'condition',
        data: { field: 'conversation.assignee', operator: 'equals', value: '' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.ASSIGNEE'),
      },
      {
        type: 'condition',
        data: { field: 'conversation.team', operator: 'equals', value: '' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.TEAM'),
      },
      {
        type: 'condition',
        data: { field: 'conversation.channel', operator: 'equals', value: '' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.CONDITION.CHANNEL'),
      },
      {
        // Multi-output router. Renders as SwitchNode with N+1 handles
        // (one per case + default). Backend SwitchHandler picks the
        // first matching case via the same operators ConditionEvaluator
        // exposes.
        type: 'switch',
        data: {
          input: '',
          cases: [{ id: 'case_1', operator: 'equals', value: '' }],
          label: t(
            'PIPELINE.FLOW_BUILDER.NODE.SWITCH.LABEL',
            'Switch / Router'
          ),
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.SWITCH.LABEL', 'Switch / Router'),
      },
    ],
  },
  {
    key: 'messaging',
    label: t('PIPELINE.FLOW_BUILDER.TOOLBAR.MESSAGING'),
    icon: 'action',
    items: [
      {
        type: 'action',
        data: { type: 'send_message' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_message'),
      },
      {
        type: 'action',
        data: { type: 'send_notification' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_notification'),
      },
      {
        type: 'action',
        data: { type: 'add_note' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.add_note'),
      },
      {
        type: 'action',
        data: { type: 'send_whatsapp_template' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_whatsapp_template'),
      },
      {
        type: 'action',
        data: { type: 'send_whatsapp_media' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_whatsapp_media'),
      },
      {
        type: 'action',
        data: { type: 'send_attachment' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_attachment'),
      },
      {
        type: 'action',
        data: { type: 'send_email_to_team' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_email_to_team'),
      },
      {
        type: 'action',
        data: { type: 'send_email_transcript' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_email_transcript'),
      },
      {
        type: 'action',
        data: { type: 'send_csat_survey' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.send_csat_survey'),
      },
    ],
  },
  {
    key: 'conversation',
    label: t('PIPELINE.FLOW_BUILDER.TOOLBAR.CONVERSATION'),
    icon: 'action',
    items: [
      {
        type: 'action',
        data: { type: 'change_status' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.change_status'),
      },
      {
        type: 'action',
        data: { type: 'assign_agent' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.assign_agent'),
      },
      {
        type: 'action',
        data: { type: 'assign_team' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.assign_team'),
      },
      {
        type: 'action',
        data: { type: 'resolve_conversation' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.resolve_conversation'),
      },
      {
        type: 'action',
        data: { type: 'reopen_conversation' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.reopen_conversation'),
      },
      {
        type: 'action',
        data: { type: 'snooze_conversation' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.snooze_conversation'),
      },
      {
        type: 'action',
        data: { type: 'transfer_to_inbox' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.transfer_to_inbox'),
      },
      {
        type: 'action',
        data: { type: 'set_conversation_attribute' },
        label: t(
          'PIPELINE.FLOW_BUILDER.NODE.ACTION.set_conversation_attribute'
        ),
      },
      {
        type: 'action',
        data: { type: 'mute_conversation' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.mute_conversation'),
      },
      {
        type: 'action',
        data: { type: 'unmute_conversation' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.unmute_conversation'),
      },
    ],
  },
  {
    key: 'pipeline',
    label: t('PIPELINE.FLOW_BUILDER.TOOLBAR.PIPELINE'),
    icon: 'action',
    items: [
      {
        type: 'action',
        data: { type: 'move_to_stage' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.move_to_stage'),
      },
      {
        type: 'action',
        data: { type: 'change_priority' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.change_priority'),
      },
      {
        type: 'action',
        data: { type: 'add_label' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.add_label'),
      },
      {
        type: 'action',
        data: { type: 'remove_label' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.remove_label'),
      },
      {
        type: 'action',
        data: { type: 'create_pipeline_card' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.create_pipeline_card'),
      },
      {
        type: 'action',
        data: { type: 'create_task' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.create_task'),
      },
      {
        type: 'action',
        data: { type: 'add_tag' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.add_tag'),
      },
    ],
  },
  {
    key: 'advanced',
    label: t('PIPELINE.FLOW_BUILDER.TOOLBAR.ADVANCED'),
    icon: 'action',
    items: [
      {
        type: 'action',
        data: { type: 'http_request' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.http_request'),
      },
      {
        type: 'action',
        data: { type: 'http_response' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.http_response'),
      },
      {
        type: 'action',
        data: { type: 'set_variable' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.set_variable'),
      },
      {
        // Call another automation as a sub-flow (reusable building block).
        type: 'action',
        data: { type: 'call_automation' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.call_automation'),
      },
      {
        type: 'action',
        data: { type: 'wait' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.wait'),
      },
      {
        type: 'action',
        data: { type: 'update_contact' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.update_contact'),
      },
      {
        type: 'action',
        data: { type: 'trigger_webhook' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.trigger_webhook'),
      },
      {
        type: 'action',
        data: { type: 'call_captain_ai' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.call_captain_ai'),
      },
      {
        type: 'action',
        data: { type: 'create_google_event' },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ACTION.create_google_event'),
      },
    ],
  },
  {
    key: 'utilities',
    label: t('PIPELINE.FLOW_BUILDER.TOOLBAR.UTILITIES'),
    icon: 'utility',
    items: [
      {
        type: 'annotation',
        data: {
          type: 'annotation',
          content: '',
          label: t('PIPELINE.FLOW_BUILDER.NODE.ANNOTATION.LABEL'),
        },
        label: t('PIPELINE.FLOW_BUILDER.NODE.ANNOTATION.LABEL'),
      },
    ],
  },
];

// Returns a flat array of all catalog items with category metadata,
// suitable for command-palette-style fuzzy search.
export const getFlatNodeCatalog = t => {
  const categories = getNodeCatalog(t);
  return categories.flatMap(category =>
    category.items.map(item => ({
      ...item,
      categoryKey: category.key,
      categoryLabel: category.label,
    }))
  );
};
